import { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Task, Employee, TeamDefinition } from '../../../types';
import {
  useEmployees,
  useTeams,
  useRoadmapActions,
  recalculateSchedule,
} from '../../../stores';
import { fuzzyScore } from '../../utils/fuzzySearch';
import { EmployeeRow } from './EmployeeRow';

interface AssigneePickerPopoverProps {
  x: number;
  y: number;
  task: Task;
  onClose: () => void;
}

function hasEstimate(task: Task, employeeId: string): boolean {
  if (task.estimateMode === 'default') {
    return !!task.defaultEstimate;
  }
  return !!task.estimates[employeeId];
}

export const AssigneePickerPopover = memo(function AssigneePickerPopover({
  x,
  y,
  task,
  onClose,
}: AssigneePickerPopoverProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);

  const employees = useEmployees();
  const teams = useTeams();
  const { addAssignee, removeAssignee } = useRoadmapActions();

  // Adjust position to stay within viewport
  const adjustedX = Math.min(x, window.innerWidth - 340);
  const adjustedY = Math.min(y, window.innerHeight - 420);

  // Autofocus search input
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  // Close on click outside or ESC
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Build team lookup
  const teamMap = useMemo(() => {
    const map: Record<string, TeamDefinition> = {};
    for (const t of teams) {
      map[t.id] = t;
    }
    return map;
  }, [teams]);

  // Filter and group employees
  const { assigned, available, noEstimate } = useMemo(() => {
    const assignedSet = new Set(task.assigneeIds);

    let filtered = employees;

    // Team filter
    if (selectedTeam) {
      filtered = filtered.filter(e => e.team === selectedTeam);
    }

    // Fuzzy search
    if (searchQuery.trim()) {
      filtered = filtered.filter(e => {
        const teamName = teamMap[e.team]?.name || '';
        const searchText = `${e.name} ${teamName} ${e.skills || ''}`;
        return fuzzyScore(searchQuery.trim(), searchText) > 0;
      });
    }

    const assigned: Employee[] = [];
    const available: Employee[] = [];
    const noEstimate: Employee[] = [];

    for (const emp of filtered) {
      if (assignedSet.has(emp.id)) {
        assigned.push(emp);
      } else if (hasEstimate(task, emp.id)) {
        available.push(emp);
      } else {
        noEstimate.push(emp);
      }
    }

    // Sort alphabetically within groups
    const sort = (a: Employee, b: Employee) => a.name.localeCompare(b.name);
    assigned.sort(sort);
    available.sort(sort);
    noEstimate.sort(sort);

    return { assigned, available, noEstimate };
  }, [employees, task, selectedTeam, searchQuery, teamMap]);

  const handleAssign = useCallback((employeeId: string) => {
    addAssignee(task.id, employeeId);
    setTimeout(() => recalculateSchedule(), 0);
  }, [addAssignee, task.id]);

  const handleUnassign = useCallback((employeeId: string) => {
    removeAssignee(task.id, employeeId);
    setTimeout(() => recalculateSchedule(), 0);
  }, [removeAssignee, task.id]);

  // Teams that have employees
  const activeTeams = useMemo(() => {
    const teamIds = new Set(employees.map(e => e.team));
    return teams.filter(t => teamIds.has(t.id));
  }, [employees, teams]);

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: adjustedY,
        zIndex: 9999,
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
        width: 320,
        maxHeight: 420,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search */}
      <div style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ position: 'relative' }}>
          <svg
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('boardv3.assigneePicker.searchPlaceholder')}
            style={{
              width: '100%',
              paddingLeft: 32,
              paddingRight: 12,
              paddingTop: 6,
              paddingBottom: 6,
              fontSize: 14,
              color: '#1f2937',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              outline: 'none',
              backgroundColor: '#fff',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Team Filter Pills */}
      <div style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 4, overflowX: 'auto', flexShrink: 0 }}>
        <button
          onClick={() => setSelectedTeam(null)}
          style={{
            padding: '2px 8px',
            borderRadius: 9999,
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: selectedTeam === null ? '#1f2937' : '#f3f4f6',
            color: selectedTeam === null ? '#fff' : '#4b5563',
          }}
        >
          {t('boardv3.assigneePicker.all')}
        </button>
        {activeTeams.map(team => (
          <button
            key={team.id}
            onClick={() => setSelectedTeam(selectedTeam === team.id ? null : team.id)}
            style={{
              padding: '2px 8px',
              borderRadius: 9999,
              fontSize: 12,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: selectedTeam === team.id ? team.color : `${team.color}20`,
              color: selectedTeam === team.id ? '#fff' : team.color,
            }}
          >
            {team.name}
          </button>
        ))}
      </div>

      {/* Employee List */}
      <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
        {/* Assigned */}
        {assigned.length > 0 && (
          <div>
            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('boardv3.assigneePicker.assigned', { count: assigned.length })}
            </div>
            {assigned.map(emp => (
              <EmployeeRow
                key={emp.id}
                employee={emp}
                team={teamMap[emp.team]}
                isAssigned
                onClick={() => handleUnassign(emp.id)}
              />
            ))}
          </div>
        )}

        {/* Available */}
        {available.length > 0 && (
          <div>
            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('boardv3.assigneePicker.available', { count: available.length })}
            </div>
            {available.map(emp => (
              <EmployeeRow
                key={emp.id}
                employee={emp}
                team={teamMap[emp.team]}
                isAssigned={false}
                onClick={() => handleAssign(emp.id)}
              />
            ))}
          </div>
        )}

        {/* No Estimate */}
        {noEstimate.length > 0 && (
          <div>
            <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('boardv3.assigneePicker.noEstimate', { count: noEstimate.length })}
            </div>
            {noEstimate.map(emp => (
              <EmployeeRow
                key={emp.id}
                employee={emp}
                team={teamMap[emp.team]}
                isAssigned={false}
                disabled
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {assigned.length === 0 && available.length === 0 && noEstimate.length === 0 && (
          <div style={{ padding: '16px 12px', fontSize: 14, color: '#9ca3af', textAlign: 'center' }}>
            {t('boardv3.assigneePicker.noEmployeesFound')}
          </div>
        )}
      </div>
    </div>
  );
});
